import * as React from 'react';
import {useTheme} from 'styled-components';
import {darken, lighten, transparentize} from 'polished';
import StateManager, {Props, StylesConfig} from 'react-select';

export enum SelectVariant {
  Default = 'default',
  Accent = 'accent',
}

export enum SelectScale {
  Small = 'small',
  Medium = 'medium',
}

export interface SelectProps extends Props {
  variant?: SelectVariant;
  scale?: SelectScale;
}

export interface OptionObject<T = string> {
  label: string;
  value: T;
}

/**
 * The select component
 * @param {SelectProps} props
 * @return {React.ReactElement<SelectProps>}
 * @constructor
 */
function Select<isMulti extends boolean = false>(
    props: SelectProps,
): React.ReactElement<SelectProps> {
  const theme = useTheme();

  /**
   * Get stylesheet
   * @param {SelectProps} props
   * @return {{}}
   */
  function getStyles(props: SelectProps) {
    return {
      container: (provided) => ({
        ...provided,
        'width': props.width
          ? props.width
          : 'auto',
      }),
      menu: (provided) => ({
        ...provided,
        'margin': '8px 0',
        'padding': '4px 0',
        'border-radius': '5px',
        'border': props.variant === SelectVariant.Default && (
          `1px solid ${transparentize(.95, theme.colors.white)}`
        ),
        'box-shadow': '5px 5px 60px rgba(24, 24, 24, .8)',
        'background': props.variant === SelectVariant.Default && (
          theme.variables.backgroundColor
        ) || props.variant === SelectVariant.Accent && (
          theme.variables.accentBackgroundColor
        ),
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        'padding': '0 4px',
      }),
      indicatorSeparator: () => ({}),
      control: (provided, state) => ({
        ...provided,
        'border': '1px solid transparent',
        'border-radius': '5px',
        'cursor': !state.isDisabled && 'pointer',
        'border-style': 'initial',
        'background': props.variant === SelectVariant.Default && (
          theme.variables.backgroundColor
        ) || props.variant === SelectVariant.Accent && (
          theme.variables.accentBackgroundColor
        ),
        ':hover': {
          'border-color': 'transparent',
          'background': props.variant === SelectVariant.Default && (
            darken(.01, theme.variables.backgroundColor)
          ) || props.variant === SelectVariant.Accent && (
            lighten(.02, theme.variables.accentBackgroundColor)
          ),
        },
        ':focus': {
          'outline': '0 0 0 2px red',
          'border-color': theme.colors.primary,
          'background': props.variant === SelectVariant.Default && (
            darken(.01, theme.variables.backgroundColor)
          ) || props.variant === SelectVariant.Accent && (
            lighten(.02, theme.variables.accentBackgroundColor)
          ),
          'box-shadow': 'none',
        },
        'transition': `${theme.variables.transitionDelay} ${theme.variables.transitionTimingFunction}`,
      }),
      valueContainer: (provided) => ({
        ...provided,
        'padding': props.scale === SelectScale.Small
            ? '9px 12px'
            : '12px 14px',
      }),
      input: (provided) => ({
        ...provided,
        'padding': '0',
        'margin': '0',
        'font-size': props.scale === SelectScale.Small
          ? '13px'
          : '15px',
        'font-weight': '400',
        'line-height': '20px',
        'color': theme.variables.color,
      }),
      singleValue: (provided) => ({
        ...provided,
        'color': theme.variables.color,
        'margin': '0',
        'font-size': props.scale === SelectScale.Small
          ? '13px'
          : '15px',
        'font-weight': '400',
        'line-height': '20px',
      }),
      option: (provided, state) => ({
        ...provided,
        'cursor': 'pointer',
        'padding': '12px 14px',
        'transition': `${theme.variables.transitionDelay} ${theme.variables.transitionTimingFunction}`,
        'background': state.isSelected ? theme.colors.primary : 'transparent',
        ':hover': {
          'background': !state.isSelected && props.variant === SelectVariant.Default && (
            lighten(.05, theme.variables.backgroundColor)
          ) || props.variant === SelectVariant.Accent && (
            lighten(.05, theme.variables.accentBackgroundColor)
          ),
        },
        ':active': {
          'background': !state.isSelected && props.variant === SelectVariant.Default && (
            lighten(.1, theme.variables.backgroundColor)
          ) || props.variant === SelectVariant.Accent && (
            lighten(.1, theme.variables.accentBackgroundColor)
          ),
        },
        ':focus': {
          'background': !state.isSelected && props.variant === SelectVariant.Default && (
            lighten(.05, theme.variables.backgroundColor)
          ) || props.variant === SelectVariant.Accent && (
            lighten(.05, theme.variables.accentBackgroundColor)
          ),
        },
      }),
      placeholder: (provided, state) => ({
        ...provided,
        'margin': '0',
        'font-size': props.scale === SelectScale.Small
          ? '13px'
          : '15px',
        'font-weight': '400',
        'line-height': '20px',
        'color': props.variant === SelectVariant.Default && (
          theme.variables.placeholderColor
        ) || props.variant === SelectVariant.Accent && (
          transparentize(.7, theme.variables.color)
        ),
        'opacity': state.isFocused && '0',
        'transition': `${theme.variables.transitionDelay} ${theme.variables.transitionTimingFunction}`,
      }),
    } as StylesConfig<OptionObject, false>;
  }

  return (
    <StateManager
      {...props}
      styles={getStyles(props)}
    />
  );
}

Select.defaultProps = {
  variant: SelectVariant.Default,
};

export default Select;
